<template>
  <div class="trainer-academy">
    <div class="academy-card">
      <div class="card-header">
        <div class="header-icon">
          <BookIcon width="48" height="48" color="#FFFFFF" stroke-width="2" />
        </div>
        <h1 class="card-title">Edm8ker Trainer Academy</h1>
        <p class="card-subtitle">
          The Edm8ker Trainer Academy enables prospective trainers to complete
          modules that qualify you as a certified trainer, eligible for contract
          opportunities.
        </p>
      </div>

      <div class="card-content">
        <div class="intro-section">
          <p class="intro-text">
            This short course will walk you through what it means to be an
            edm8ker trainer. From our teaching philosophy, to practical
            facilitation tips that help you bring lessons to life.
          </p>
          <p class="intro-text">
            By the end of this course, you should be equipped and ready to guide
            students in discovering the joy of making.
          </p>
        </div>

        <h2 class="benefits-title">What You'll Get</h2>

        <div class="benefits-grid">
          <div class="benefit-card">
            <div class="benefit-icon" style="background-color: #ededfc">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                  stroke="#4C4DDC"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <h3 class="benefit-title">Certification Modules</h3>
            <p class="benefit-description">
              Complete structured learning paths
            </p>
          </div>

          <div class="benefit-card">
            <div class="benefit-icon" style="background-color: #fce6f0">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                  stroke="#E00069"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <h3 class="benefit-title">Expert Recognition</h3>
            <p class="benefit-description">
              Earn credentials as a certified trainer
            </p>
          </div>

          <div class="benefit-card">
            <div class="benefit-icon" style="background-color: #e6f4ef">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                  stroke="#00925E"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <h3 class="benefit-title">Contract Opportunities</h3>
            <p class="benefit-description">
              Access exclusive training contracts
            </p>
          </div>
        </div>

        <div class="button-container">
          <ActionButton
            @click="handleBack"
            variant="outline"
            class="back-button"
            style="margin-bottom: 0px"
          >
            Back</ActionButton
          >
          <ActionButton
            @click="handleGoToAcademy"
            variant="primary"
            class="academy-button"
            >Go to Trainer Academy
          </ActionButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import ActionButton from "@/components/common/ActionButton.vue";
import BookIcon from "@/components/icons/BookIcon.vue";
import { defineEmits } from "vue";
import { useRouter } from "vue-router";

const router = useRouter();

const emit = defineEmits(["back", "go-to-academy"]);

const handleBack = () => {
  emit("back");
};

const handleGoToAcademy = () => {
  router.push({ name: "TrainerAcademyPage" });
  emit("go-to-academy");
};
</script>

<style scoped>
.trainer-academy {
  background-color: #f2f3fb;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px;
  min-height: 100vh;
  width: 100%;
}

.academy-card {
  background-color: white;
  border-radius: 16px;
  width: 100%;
  max-width: 900px;
  overflow: hidden;
}

.card-header {
  background-color: var(--blue-light-2);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  padding: 32px;
  width: 100%;
}

.header-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.card-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: 45px;
  line-height: 52px;
  color: white;
  text-align: center;
  margin: 0;
}

.card-subtitle {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: white;
  text-align: center;
  margin: 0;
  letter-spacing: 0.024px;
}

.card-content {
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.intro-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.intro-text {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: #3b4648;
  margin: 0;
  letter-spacing: 0.024px;
}

.benefits-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-xl);
  text-align: center;
  line-height: 32px;
  color: var(--black-body-text);
  margin: 0;
}

.benefits-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  width: 100%;
}

.benefit-card {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
}

.benefit-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.benefit-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-big);
  line-height: 24px;
  color: var(--black-body-text);
  margin: 0;
}

.benefit-description {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-small);
  line-height: 20px;
  color: var(--neutral-700);
  margin: 0;
}

.button-container {
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.back-button {
  background-color: white;
  border: 1px solid #bac1cc;
  box-sizing: border-box;
  display: flex;
  gap: 8px;
  height: 56px;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border-radius: 12px;
  min-width: 126px;
  cursor: pointer;
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: #6c737f;
  white-space: nowrap;
  letter-spacing: 0.024px;
  transition: all 0.3s ease;
}

.back-button:hover {
  background-color: #f2f3fb;
  border-color: #0478ce;
  color: #3b4648;
}

.academy-button {
  background-color: var(--primary-blue);
  border: none;
  box-sizing: border-box;
  display: flex;
  gap: 8px;
  height: 56px;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border-radius: 12px;
  cursor: pointer;
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: white;
  white-space: nowrap;
  letter-spacing: 0.024px;
  transition: all 0.3s ease;
}

.academy-button:hover {
  background-color: #033d70;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(4, 75, 140, 0.3);
}

@media (max-width: 768px) {
  .card-title {
    font-size: 32px;
    line-height: 40px;
  }

  .card-content {
    padding: 16px;
  }

  .benefits-grid {
    gap: 8px;
  }

  .back-button,
  .academy-button {
    width: 100%;
  }

  .button-container {
    gap: 8px;
  }
  
  .benefit-title, .benefit-description {
    text-align: center;
  }
}
</style>
